import { Injectable } from '@angular/core';
import { MenuDTO } from 'src/app/shared/model/dto/MenuDTO';
import { RestaurantService } from './restaurant.service';
import { RestaurantLocalStorageService } from './restaurantLocalStorage.service';
import { OrderService } from './order.service';

@Injectable({
    providedIn: 'root'
})
export class recommendProductService {

    private restaurant: MenuDTO;
    private cachedOrders: any = [];
    private recommendedProducts: any = [];
    private recommendedItem: any;
    private availableProducts: any;

    constructor(
        private restaurantService: RestaurantService,
		private restaurantLocalStorageService: RestaurantLocalStorageService,
		private orderService: OrderService,
    ) {
        this.restaurant = this.restaurantService.restaurant;
    }

    setAvailableProducts(availableProducts) {
        let availableCategories = this.restaurant.menu.general.filter(category => 
            category.id && !category.unavailableByTemplate
        );
		

        if (availableProducts && availableProducts.length > 0) {
            this.availableProducts = availableProducts.filter(product => 
                product.unavailableByTemplate == 0 && 
                availableCategories.some(cat => cat.id === product.category_id)
            );
        }
    }

	async getRecommendedProducts() {
		await this.loadCachedOrders();
		this.setRecomendedProducts();

		return this.recommendedProducts;
    }

	async loadCachedOrders() {
		this.cachedOrders = [];
		const deviceOrders = JSON.parse(this.restaurantLocalStorageService.getItem("deviceOrders"));
		if (deviceOrders) {
			const ordersUUID = deviceOrders.reverse();
			let orderCont = 1;

			for (const orderUUID of ordersUUID) {
				if (orderCont > 5)
					break;

				await this.orderService.getCachedOrderByUUID(orderUUID).then((order: string) => {
					this.cachedOrders.push(order);
					orderCont++;
				});
			}
		}
	}

	setRecomendedProducts() {
		this.recommendedProducts = [];

		this.cachedOrders.forEach(order => {
			let foundProduct = null;

			if (this.recommendedProducts.length >= 5)
				return;

			order.items.forEach(item => {
				this.recommendedItem = item;

				if (!this.canRecommendThisProduct() || item.type != 'general')
					return;

				foundProduct = this.availableProducts.find(ap => ap.id === item.menu_item_id && ap.hasStock);

				if (foundProduct) {
					this.recommendedItem.allExtrasUnavailable = false;
					this.recommendedItem.image = foundProduct.image ?? null;
					this.recommendedItem.price = foundProduct.price;
					this.setExtras(foundProduct);

					if(this.recommendedItem.allExtrasUnavailable){
						return;
					}

					this.recommendedProducts.push(this.genericItemParse());
				}
			});
		});
	}

	checkCanShowRecommendedProducts() {
		return this.restaurant.info.show_recommended_products;
	}

    canRecommendThisProduct() {
		const recommendProductByMinimumPrice = this.restaurant.info.show_recommended_products_by_minimum_price;
		const recommendProductMinimumValue = this.restaurant.info.show_recommended_products_minimum_value;
		const itemAlreadyExists = this.recommendedProducts.find(p => p.id === this.recommendedItem.menu_item_id) ? true : false;

		return (!itemAlreadyExists && (!recommendProductByMinimumPrice || recommendProductMinimumValue === null || (recommendProductByMinimumPrice && this.recommendedItem.subTotal >= recommendProductMinimumValue)));
	}

	setExtras(foundedProduct) {
		this.recommendedItem.hasMissingRequiredExtras = false;
		this.recommendedItem.shouldOpenExtrasScreen = false;
	
		const extraInfo = this.buildExtraInfo(foundedProduct);
		const extras = this.processRecommendedItemExtras();
		const hadExtrasLastOrder = this.recommendedItem.extras.length > 0;
	
		this.recommendedItem.extras = extras;
		this.recommendedItem.extraInfo = extraInfo;
		this.recommendedItem.shouldOpenExtrasScreen = (!hadExtrasLastOrder && extraInfo.length > 0);
	
		this.validateRequiredExtras(extraInfo, extras);
	}
	
	private buildExtraInfo(foundedProduct) {
		const extraInfo = [];
	
		if (foundedProduct.extras) {
			foundedProduct.extras.forEach(extraID => {
				const extraCategory = this.restaurant.menu.extras.find(e => e.id === extraID);
				const hasAvailableOptions = extraCategory?.options?.some(option => 
					option.available && option.hasStock
				);
				if (hasAvailableOptions) {
					extraInfo.push(extraCategory);
				}
			});
		}
	
		return extraInfo;
	}
	
	private processRecommendedItemExtras() {
		const extras = [];
	
		this.recommendedItem.extras.forEach(extra => {
			const foundedExtra = this.getOptionFromMenuByID(extra.menu_item_id);
			const extraCategory = this.getExtraCategoryFromMenuByExtraID(extra.menu_item_id);
	
			if (!foundedExtra)
				return;
	
			extras.push({
				...extra,
				isAvailable: foundedExtra.available && foundedExtra.hasStock,
				parent: extraCategory,
				price: foundedExtra.price
			});
		});
	
		return extras;
	}
	
	private validateRequiredExtras(extraInfo, extras) {
		const requiredExtras = this.getRequiredExtras(extraInfo);
	
		if (requiredExtras.length === 0) {
			return;
		}
	
		const selectedAvailableExtras = extras.filter(e => e.isAvailable);
		
		const missingRequiredExtras = this.getMissingRequiredExtras(requiredExtras, selectedAvailableExtras);
		if (missingRequiredExtras.length === 0) {
			return;
		}
		
		const minPrice = this.restaurantService.calculateMinimalPriceFromExtraCategories(missingRequiredExtras);
		this.recommendedItem.hasMissingRequiredExtras = true;
		this.recommendedItem.requiredExtrasMinPrice = minPrice;
	}

	// OP-1064: `required` só vale para grupo "único" (type 1); em grupo múltiplo a
	// obrigatoriedade é qty_min > 0. Ler `required` no múltiplo exigia grupo OPCIONAL
	// e inflava o requiredExtrasMinPrice do item recomendado.
	getRequiredExtras(extraInfo) {
		return extraInfo.filter(extra =>
			extra.type === 1 ? extra.required === 1 : extra.qty_min > 0
		);
	}

	getMissingRequiredExtras(requiredExtras, selectedExtras) {
		if (selectedExtras.length === 0) {
			return requiredExtras;
		}
	
		return requiredExtras.filter(requiredCat => {
			let totalQty = 0;
			let hasItemAboveMaxPerItem = false;
	
			for (const selected of selectedExtras) {
				const extraCat = this.getExtraCategoryFromMenuByExtraID(selected.menu_item_id);
	
				if (extraCat?.id !== requiredCat.id) continue;
	
				const qty = selected.quantity || 0;
	
				totalQty += qty;
	
				if (requiredCat.qtyPerItem > 0 && qty > requiredCat.qtyPerItem) {
					hasItemAboveMaxPerItem = true;
				}
			}
	
			const missingMin = requiredCat.qtyMin > 0 && totalQty < requiredCat.qtyMin;
			const exceedingMax = requiredCat.qtyMax > 0 && totalQty > requiredCat.qtyMax;
			const exceedingPerItem = hasItemAboveMaxPerItem;
	
			return totalQty === 0 || missingMin || exceedingMax || exceedingPerItem;
		});
	}

    baseParseItem() {
		return {
			id: this.recommendedItem.menu_item_id,
			name: this.recommendedItem.name,
			qty: this.recommendedItem.quantity,
			subTotal: this.recommendedItem.subTotal,
			price: this.recommendedItem.price,
			type: this.recommendedItem.type,
			notes: this.recommendedItem.notes,
			extraInfo: this.recommendedItem.extraInfo,
			image: this.recommendedItem.image,
			hasMissingRequiredExtras: this.recommendedItem.hasMissingRequiredExtras,
			shouldOpenExtrasScreen: this.recommendedItem.shouldOpenExtrasScreen,
		}
	}

	genericItemParse() {
		let extras = [];
		let totalExtra = 0;

		if (this.recommendedItem.extras.length > 0) {
			this.recommendedItem.extras.forEach(extra => {
				extras.push({
					id: extra.menu_item_id,
					menu_item_id: extra.menu_item_id,
					name: extra.name,
					price: extra.price,
					qty: extra.quantity,
					isAvailable: extra.isAvailable,
					parent: extra.parent
				});

				if(extra.isAvailable) {
					totalExtra += (extra.quantity * extra.price);
				}	
			});
		}

		const basePrice = this.recommendedItem.price * this.recommendedItem.quantity;

		this.recommendedItem.subTotal = basePrice + (
		  this.recommendedItem.hasMissingRequiredExtras
			? (this.recommendedItem.requiredExtrasMinPrice || 0)
			: totalExtra
		);

		return {...this.baseParseItem(), ...{
			extras: extras
		}}
	}

	getOptionFromMenuByID(id) {
		let foundedExtra = null;

		this.restaurant.menu.extras.find(extraCategory => {
			let wasFoundedExtra = extraCategory.options.find(option => option.id == id);
			
			if (wasFoundedExtra)
				foundedExtra = wasFoundedExtra;
		});

		return foundedExtra;
	}

	getExtraCategoryFromMenuByExtraID(extraID) {
		return this.restaurant.menu.extras.find(e => {
			return e.options.find(o => o.id === extraID);
		});
	}
}
